import { useEffect, useState } from 'react'
import { formatStatusLabel } from '../../../lib/format'
import { staffApi } from '../../../api/staff'
import type { StaffScheduleData, StaffPatientContextInsight, StaffPatientSegmentsInsight } from '../../../types/plans'
import type { TenantBootstrapModel } from '@unified-lk/tenant-config'
import type { AuthSession } from '../../../types/plans'
import { SecretLoginLinkDialog } from '../../SecretLoginLinkDialog'

type Appointment = StaffScheduleData['appointments'][number]

export function StaffScheduleVisitPopover({
  appointment,
  bootstrap,
  session,
  onClose,
  canIssueSecretLinks = false,
  autoOpenSecretLinks = false,
  onIssuePatientSecretLink,
}: {
  appointment: Appointment
  bootstrap: TenantBootstrapModel
  session: AuthSession
  onClose: () => void
  canIssueSecretLinks?: boolean
  autoOpenSecretLinks?: boolean
  onIssuePatientSecretLink?: (patientId: number) => Promise<string>
}) {
  const [issuing, setIssuing] = useState(false)
  const [issueError, setIssueError] = useState<string | null>(null)
  const [issuedHint, setIssuedHint] = useState<string | null>(null)
  const [issuedUrl, setIssuedUrl] = useState<string | null>(null)
  const [segmentsInsight, setSegmentsInsight] = useState<StaffPatientSegmentsInsight | null>(null)
  const [contextInsight, setContextInsight] = useState<StaffPatientContextInsight | null>(null)
  const [insightsLoading, setInsightsLoading] = useState(false)
  const [insightsError, setInsightsError] = useState<string | null>(null)
  const hasAppointmentComment = Boolean(appointment.appointment_comment?.trim())
  const hasCardComment = Boolean(appointment.card_comment?.trim())
  const fallbackNote = appointment.note?.trim()

  useEffect(() => {
    if (!appointment.patient_id) {
      return
    }

    let cancelled = false
    setInsightsLoading(true)
    setInsightsError(null)

    void (async () => {
      try {
        const [segments, context] = await Promise.all([
          staffApi.getPatientSegmentsInsight(bootstrap, session, appointment.patient_id!),
          staffApi.getPatientContextInsight(bootstrap, session, appointment.patient_id!),
        ])
        if (!cancelled) {
          setSegmentsInsight(segments)
          setContextInsight(context)
        }
      } catch (reason) {
        if (!cancelled) {
          setInsightsError(reason instanceof Error ? reason.message : 'Не удалось загрузить контекст пациента.')
        }
      } finally {
        if (!cancelled) {
          setInsightsLoading(false)
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [appointment.patient_id, bootstrap, session])

  async function handleSecretLogin() {
    if (!onIssuePatientSecretLink || !appointment.patient_id) return
    setIssuing(true)
    setIssueError(null)
    setIssuedHint(null)
    try {
      const url = await onIssuePatientSecretLink(appointment.patient_id)
      if (autoOpenSecretLinks) {
        try {
          await navigator.clipboard.writeText(url)
          setIssuedHint('Ссылка скопирована и откроется в новой вкладке.')
        } catch {
          setIssuedHint('Ссылка готова — открываем в новой вкладке.')
        }
        window.open(url, '_blank', 'noopener,noreferrer')
        return
      }
      setIssuedUrl(url)
      setIssuedHint('Ссылка готова для копирования.')
    } catch (reason) {
      setIssueError(reason instanceof Error ? reason.message : 'Не удалось сформировать secret-ссылку.')
    } finally {
      setIssuing(false)
    }
  }

  const visitChipClass =
    appointment.visit_kind === 'primary'
      ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
      : appointment.visit_kind === 'repeat'
        ? 'border-amber-300 bg-amber-50 text-amber-700'
        : 'border-slate-300 bg-slate-50 text-slate-600'

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-5 shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">
              {appointment.date} · {appointment.start_time}–{appointment.end_time}
            </p>
            <h3 className="mt-1 text-lg font-semibold text-slate-900">
              {appointment.patient_name}
            </h3>
            {appointment.visit_label ? (
              <span
                className={`mt-1 inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${visitChipClass}`}
              >
                {appointment.visit_label}
              </span>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            aria-label="Закрыть"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <dl className="grid grid-cols-[110px_minmax(0,1fr)] gap-y-2 text-sm">
          <dt className="text-slate-500">Врач</dt>
          <dd className="text-slate-900">{appointment.doctor_name?.trim() || 'Не указан'}</dd>

          <dt className="text-slate-500">Услуга</dt>
          <dd className="text-slate-900">{appointment.service_name}</dd>

          <dt className="text-slate-500">Филиал</dt>
          <dd className="text-slate-900">{appointment.branch_name}</dd>

          <dt className="text-slate-500">Кабинет</dt>
          <dd className="text-slate-900">{appointment.room_name}</dd>

          <dt className="text-slate-500">Статус</dt>
          <dd className="text-slate-900">{formatStatusLabel(appointment.status)}</dd>
        </dl>

        {(hasAppointmentComment || hasCardComment || fallbackNote) ? (
          <div className="mt-4 space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm leading-5 text-slate-700">
            {hasAppointmentComment ? (
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500">
                  Комментарий к приему
                </p>
                <p className="mt-1 whitespace-pre-wrap break-words">{appointment.appointment_comment}</p>
              </div>
            ) : null}
            {hasCardComment ? (
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500">
                  Комментарий к карте
                </p>
                <p className="mt-1 whitespace-pre-wrap break-words">{appointment.card_comment}</p>
              </div>
            ) : null}
            {!hasAppointmentComment && !hasCardComment && fallbackNote ? (
              <p className="whitespace-pre-wrap break-words">{fallbackNote}</p>
            ) : null}
          </div>
        ) : null}

        {appointment.patient_id ? (
          <div className="mt-4 space-y-3 border-t border-slate-200 pt-3 text-sm">
            {insightsLoading ? (
              <p className="text-slate-500">Загружаем сегменты и контекст...</p>
            ) : null}
            {insightsError ? (
              <p className="text-rose-700">{insightsError}</p>
            ) : null}
            {segmentsInsight ? (
              <div className="grid gap-2">
                <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500">Сегменты</p>
                {segmentsInsight.segments.length === 0 ? (
                  <p className="text-slate-600">Пациент не состоит в сегментах.</p>
                ) : (
                  <>
                    <div className="flex flex-wrap gap-1.5">
                      {segmentsInsight.segments.map((segment) => (
                        <span
                          key={`${segment.segment_id ?? segment.name}`}
                          className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-700"
                        >
                          {segment.name}
                        </span>
                      ))}
                    </div>
                    {segmentsInsight.segments_guidance ? (
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500">Что делать</p>
                        <p className="mt-1 whitespace-pre-wrap text-slate-800">{segmentsInsight.segments_guidance}</p>
                      </div>
                    ) : null}
                  </>
                )}
              </div>
            ) : null}
            {contextInsight ? (
              <div className="grid gap-2">
                <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500">Контекст пациента</p>
                <p className="whitespace-pre-wrap text-slate-800">{contextInsight.summary_preview ?? contextInsight.summary}</p>
                {contextInsight.meta_summary_text ? (
                  <p className="whitespace-pre-wrap text-slate-600">{contextInsight.meta_summary_text}</p>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}

        {canIssueSecretLinks && onIssuePatientSecretLink && appointment.patient_id ? (
          <div className="mt-4 flex flex-col items-end gap-2">
            <button
              type="button"
              onClick={() => void handleSecretLogin()}
              disabled={issuing}
              className="rounded-lg border border-sky-300 bg-sky-50 px-3 py-1.5 text-sm font-medium text-sky-700 hover:bg-sky-100 disabled:cursor-not-allowed disabled:opacity-60"
              title="Сформировать secret-ссылку входа для пациента"
            >
              {issuing ? 'Формируем secret...' : 'Secret вход в ЛК пациента'}
            </button>
            {issuedHint ? (
              <span className="text-xs text-emerald-700">{issuedHint}</span>
            ) : null}
            {issueError ? (
              <span className="text-xs text-rose-700">{issueError}</span>
            ) : null}
          </div>
        ) : null}
        {issuedUrl ? (
          <SecretLoginLinkDialog
            url={issuedUrl}
            targetName={appointment.patient_name}
            onClose={() => setIssuedUrl(null)}
          />
        ) : null}
      </div>
    </div>
  )
}
